import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Link,
} from '@mui/material';
import { CheckCircle, Token, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const steps = ['Personal Details', 'OTP Verification', 'Welcome'];

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();

  const prefill = location.state?.prefill || {};

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1 fields
  const [fullName, setFullName] = useState(prefill.fullName || '');
  const [email, setEmail] = useState(prefill.email || '');
  const [phone, setPhone] = useState(prefill.phone || '');
  const [icNumber, setIcNumber] = useState(prefill.icNumber || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2 fields
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const otpRefs = useRef([]);

  // Step 3 fields
  const [registeredUser, setRegisteredUser] = useState(null);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (activeStep !== 1) return;
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, activeStep]);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleContinue = async () => {
    setError('');
    if (!fullName.trim() || !email.trim() || !phone.trim() || !icNumber.trim()) {
      setError('Please fill in all fields');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    // Check email not already taken
    try {
      const db = (await import('../../data/demoDatabase')).default;
      const existing = db.users.getAll().find(
        (u) => u.email && u.email.toLowerCase() === email.trim().toLowerCase()
      );
      if (existing) {
        setError('An account with this email already exists');
        return;
      }
    } catch (e) {
      // If the db module can't be loaded here we'll catch the error at register time
    }
    setActiveStep(1);
    setCountdown(60);
    setCanResend(false);
  };

  const handleVerifyOtp = async () => {
    setError('');
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }
    if (otpCode !== '123456') {
      setError('Invalid verification code. For demo, use: 123456');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        fullName,
        email,
        phone,
        icNumber,
        password,
      };
      const user = await register(userData);
      if (!user) {
        setError('Registration failed. Please try again.');
        return;
      }
      setRegisteredUser(user);
      setActiveStep(2);
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    setOtp(['', '', '', '', '', '']);
    setCountdown(60);
    setCanResend(false);
    otpRefs.current[0]?.focus();
  };

  const maskedPhone = phone ? `+60${'X'.repeat(Math.max(0, phone.length - 2))}${phone.slice(-2)}` : '';

  const renderStep1 = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 0.5 }}>
        Create Your Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Join the iMoney referral network and start earning
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Phone Number"
        value={phone}
        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
        InputProps={{
          startAdornment: <InputAdornment position="start">+60</InputAdornment>,
        }}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="IC Number"
        value={icNumber}
        onChange={(e) => setIcNumber(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        helperText="At least 6 characters"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" size="small">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Confirm Password"
        type={showPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleContinue}
        sx={{
          py: 1.5,
          backgroundColor: '#1B365D',
          fontWeight: 600,
          fontSize: '1rem',
          textTransform: 'none',
          borderRadius: 2,
          '&:hover': { backgroundColor: '#142847' },
        }}
      >
        Continue
      </Button>
    </>
  );

  const renderStep2 = () => (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 0.5 }}>
        Verify Your Phone
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We've sent a verification code to your phone {maskedPhone}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* OTP Input */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 3 }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => (otpRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: 700,
                padding: '12px 0',
              },
            }}
            sx={{
              width: 48,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        ))}
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        For demo, use code: <strong>123456</strong>
      </Alert>

      <Button
        fullWidth
        variant="contained"
        onClick={handleVerifyOtp}
        disabled={loading}
        sx={{
          py: 1.5,
          backgroundColor: '#1B365D',
          fontWeight: 600,
          fontSize: '1rem',
          textTransform: 'none',
          borderRadius: 2,
          mb: 2,
          '&:hover': { backgroundColor: '#142847' },
        }}
      >
        {loading ? 'Verifying...' : 'Verify'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        {canResend ? (
          <Link
            component="button"
            variant="body2"
            onClick={handleResendOtp}
            sx={{ color: '#1B365D', fontWeight: 600, textDecoration: 'none' }}
          >
            Resend OTP
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Resend OTP in {countdown}s
          </Typography>
        )}
      </Box>
    </>
  );

  const renderStep3 = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CheckCircle sx={{ fontSize: 64, color: '#00B894', mb: 2 }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 1 }}>
        Welcome to iMoney!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your account has been created successfully
      </Typography>

      <Box
        sx={{
          backgroundColor: '#F5F7FA',
          borderRadius: 2,
          p: 2.5,
          mb: 3,
          textAlign: 'left',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Member ID
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {registeredUser?.memberId || 'IM-XXXXXX'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Referral Code
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {registeredUser?.referralCode || `+60${phone}`}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Your referral code is your phone number: <strong>+60{phone}</strong>
      </Typography>

      {/* Token welcome bonus */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          backgroundColor: '#FFF8E1',
          borderRadius: 2,
          p: 1.5,
          mb: 3,
        }}
      >
        <Token sx={{ color: '#F9A825' }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#F57F17' }}>
          You've earned 5 welcome tokens!
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={() => navigate('/dashboard')}
        sx={{
          py: 1.5,
          backgroundColor: '#00B894',
          fontWeight: 600,
          fontSize: '1rem',
          textTransform: 'none',
          borderRadius: 2,
          '&:hover': { backgroundColor: '#00A381' },
        }}
      >
        Start Earning Rewards
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 100%)',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 480 }}>
        {/* Logo */}
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: 1,
            mb: 3,
          }}
        >
          iMoney
        </Typography>

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' },
            '& .MuiStepLabel-label.Mui-active': { color: '#fff' },
            '& .MuiStepLabel-label.Mui-completed': { color: '#00B894' },
            '& .MuiStepIcon-root': { color: 'rgba(255,255,255,0.3)' },
            '& .MuiStepIcon-root.Mui-active': { color: '#00B894' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#00B894' },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Content Card */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <CardContent sx={{ p: 4 }}>
            {activeStep === 0 && renderStep1()}
            {activeStep === 1 && renderStep2()}
            {activeStep === 2 && renderStep3()}
          </CardContent>
        </Card>

        {/* Login link */}
        {activeStep < 2 && (
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'rgba(255,255,255,0.85)' }}>
            Already have an account?{' '}
            <Link
              component="button"
              onClick={() => navigate('/login')}
              sx={{ color: '#00B894', fontWeight: 600, textDecoration: 'none' }}
            >
              Login
            </Link>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RegisterPage;
